import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ToggleState {
  whatIsRandomPack: boolean;
  price: boolean;
  howToSell: boolean;
  reservationConfirmation: boolean;
  feesAndSettlement: boolean;
  noFoodLeft: boolean;
  leftoverFood: boolean;
  howToStop: boolean;
}

interface Question {
  key: keyof ToggleState;
  title: string;
  element: JSX.Element;
}

const PreOrderFAQ = () => {
  const navigate = useNavigate();
  const [toggles, setToggles] = useState<ToggleState>({
    whatIsRandomPack: false,
    price: false,
    howToSell: false,
    reservationConfirmation: false,
    feesAndSettlement: false,
    noFoodLeft: false,
    leftoverFood: false,
    howToStop: false,
  });

  const handleToggle = (key: keyof ToggleState) => {
    setToggles((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleClick = () => {
    navigate("/boss/pre-order/home");
  };

  const questions: Question[] = [
    {
      key: "whatIsRandomPack",
      title: "랜덤으로 담아 판매하는 건가요?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            네, 당일 남는 상품을 랜덤으로 담아 판매하시면 됩니다.
          </p>
          <p className="text-primary font-medium">
            (랜덤이기에 더 높은 할인율이 적용됩니다.)
          </p>
        </div>
      ),
    },
    {
      key: "price",
      title: "가격은 얼마인가요?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            랜덤팩의 가격은 마음대로 설정이 가능하며,{" "}
            <span className="text-primary font-medium">
              정가의 60% 할인된 가격으로 판매
            </span>
            됩니다.
          </p>
          <p className="text-gray-600">(할인율은 조정 불가)</p>
        </div>
      ),
    },
    {
      key: "howToSell",
      title: "어떻게 판매하나요?",
      element: (
        <div className="space-y-4">
          <p className="text-gray-800 leading-relaxed">
            예약 판매 설정(가격, 개수, 설명)과 픽업 시간 설정을 하시면 고객
            화면에 바로 노출됩니다.
          </p>
          <div className="bg-green-50 p-4 rounded-xl space-y-4 border-2 border-green-200">
            <p className="font-medium text-green-800">설정 순서</p>
            <img
              src="https://cdn-mahi.com/250331-howToSell.png?q=80"
              alt="예약 판매 설정 순서"
              className="w-48 border aspect-[190/402] object-cover"
            />
            <ul className="mt-2 space-y-2 text-green-700">
              <li>1. 가격과 수량 설정</li>
              <li>2. 픽업 가능 시간 설정</li>
              <li>3. 상품 설명 작성</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      key: "reservationConfirmation",
      title: "예약확정, 예약취소는 어떻게 하나요?",
      element: (
        <div className="space-y-3">
          <img
            src="https://cdn-mahi.com/250331-reservationConfirmation.png?q=80"
            alt="예약확정, 예약취소"
            className="w-full border aspect-square object-cover"
          />
          <p className="text-gray-800 leading-relaxed">
            사장님께서 픽업 시간{" "}
            <span className="font-medium">30분 전까지</span> 자유롭게 취소하실
            수 있습니다.
          </p>
          <p className="text-primary font-medium">
            미취소 시 자동 확정되어 손님에게 알림이 갑니다.
          </p>
          <p className="text-gray-800">또한, 30분 전 조기 확정도 가능합니다.</p>
        </div>
      ),
    },
    {
      key: "feesAndSettlement",
      title: "수수료 및 정산은 어떻게 되나요?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            <span className="text-primary font-medium">
              수수료는 할인가의 9% 고정
            </span>
          </p>
          <p className="text-gray-800">
            정산은 전월 거래내역 기준으로 월 1회 자동 정산되며, 거래내역은
            부가세 신고 참고자료로 활용하실 수 있습니다.
          </p>
        </div>
      ),
    },
    {
      key: "noFoodLeft",
      title: "남은 음식이 없다면?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            사장님께서 픽업 시간{" "}
            <span className="font-medium">30분 전까지</span> 자유롭게 주문
            취소하실 수 있습니다.
          </p>
          <p className="text-primary font-medium">
            취소하지 않으면 자동으로 예약 확정 알림이 고객에게 전송됩니다.
          </p>
        </div>
      ),
    },
    {
      key: "leftoverFood",
      title: "남은 음식이 더 있다면?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            예약 고객들에게 판매하고도 상품이 남았다면, 남은 상품을 직접 업로드
            해보세요!
          </p>
          <p className="text-gray-800">직접 판매와 동시에 사용이 가능합니다.</p>
          <p className="text-primary font-medium">
            (단, 예약 손님께 먼저 상품을 드려야 합니다!)
          </p>
        </div>
      ),
    },
    {
      key: "howToStop",
      title: "판매를 중단하고 싶다면?",
      element: (
        <div className="space-y-3">
          <p className="text-gray-800 leading-relaxed">
            예약 판매를 중단하고 싶다면{" "}
            <span className="font-medium">[픽업시간 설정]</span>에서 요일 선택을
            모두 해제해주세요.
          </p>
          <p className="text-primary font-medium">즉시 판매가 중단됩니다.</p>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full overflow-y-auto max-w-[480px] mx-auto bg-white">
      <div className="flex flex-col p-4 w-full space-y-6">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-gray-800">예약 판매</h1>
          <p className="text-gray-600">아래 질문들을 클릭해서 살펴볼까요?</p>
        </div>
        <div className="space-y-3">
          {questions.map(({ key, title, element }) => (
            <div
              key={key}
              className="rounded-xl border shadow overflow-hidden bg-white"
            >
              <button
                onClick={() => handleToggle(key as keyof ToggleState)}
                className="w-full p-4 flex justify-between items-center active:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-800 text-left">
                  {title}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    toggles[key as keyof ToggleState] ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {toggles[key as keyof ToggleState] && (
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                  {element}
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={handleClick}
          className="w-full py-3.5 bg-primary text-white font-semibold rounded-2xl transition duration-150 active:brightness-90 pointer-events-auto"
        >
          홈으로 이동
        </button>
      </div>
    </div>
  );
};

export default PreOrderFAQ;
